<script lang="ts">
	import { LabelV2, LinearGauge, PlainTable, Text } from '@Shelly/shelly-design-system';
	import type { PeakMeterRow } from '../helpers/energy_peak_helpers';
	import EmPeakDeviceAvatar from './EmPeakDeviceAvatar.svelte';

	export let rows: PeakMeterRow[] = [];

	$: maxWatts = Math.max(rows[0]?.strongest.liveWatts ?? 0, 1);
	$: topRow = rows[0] ?? null;

	const timeOnly = (value: string | undefined): string => value?.split(' ')[0] || '—';
	const phaseColors = ['--color-lightblue', '--color-green', '--color-purple'] as const;
	const phaseColor = (channel: number): (typeof phaseColors)[number] | '--color-primary' =>
		phaseColors[channel] ?? '--color-primary';
</script>

{#if topRow}
	<div class="em-peak-meter">
		<div class="em-peak-meter-summary">
			<div class="em-peak-meter-summary-device">
				<EmPeakDeviceAvatar device={topRow.panel.device} size="m" />
				<div>
					<Text styleType="miniature" class="text-color-muted">Highest meter</Text>
					<Text styleType="h1" class="text-600">{topRow.panel.deviceName}</Text>
				</div>
			</div>
			<div class="em-peak-meter-summary-value">
				<Text styleType="h1" class="text-600">{topRow.strongest.live?.value ?? '—'}</Text>
				<Text styleType="miniature" class="text-color-muted">
					{topRow.strongest.label}
				</Text>
			</div>
			<div>
				<Text styleType="miniature" class="text-color-muted">5 min avg</Text>
				<Text styleType="h2" class="text-600">{topRow.strongest.avg5?.value ?? '—'}</Text>
			</div>
			<div>
				<Text styleType="miniature" class="text-color-muted">Last peak</Text>
				<Text styleType="h2" class="text-600">
					{timeOnly(topRow.strongest.live?.time)}
				</Text>
			</div>
		</div>

		<div class="em-peak-meter-mobile-list">
			{#each rows as row (row.panel.deviceId)}
				<div class="em-peak-meter-mobile-row">
					<div class="em-peak-meter-mobile-heading">
						<div class="em-peak-meter-summary-device">
							<EmPeakDeviceAvatar device={row.panel.device} size="m" />
							<div>
								<Text styleType="body" class="text-600">{row.panel.deviceName}</Text>
								<Text styleType="miniature" class="text-color-muted">
									{timeOnly(row.strongest.live?.time)}
								</Text>
							</div>
						</div>
						<LabelV2
							size="s"
							value={row.strongest.label}
							backgroundColor={phaseColor(row.strongest.channel)}
							iconColor="--color-white"
						/>
					</div>
					<div class="em-peak-meter-mobile-values">
						<div>
							<Text styleType="miniature" class="text-color-muted">Live peak</Text>
							<Text styleType="h2" class="text-600"
								>{row.strongest.live?.value ?? '—'}</Text
							>
						</div>
						<div>
							<Text styleType="miniature" class="text-color-muted">5 min avg</Text>
							<Text styleType="h2" class="text-600"
								>{row.strongest.avg5?.value ?? '—'}</Text
							>
						</div>
					</div>
					<div class="em-peak-meter-scale">
						<LinearGauge
							type="incremented"
							size="s"
							minValue={0}
							maxValue={maxWatts}
							hideMinMaxValue
							incrementedValue={{
								value: row.strongest.liveWatts,
								color: row.rank === 1 ? '--color-warning' : '--color-primary',
							}}
							a11yProps={{
								incrementedValueAriaLabel: `${row.panel.deviceName} ${row.strongest.live?.value ?? ''}`,
							}}
						/>
						<Text
							styleType="body-small"
							class="text-color-muted em-peak-meter-mobile-percent"
						>
							{Math.round(row.percent)}%
						</Text>
					</div>
				</div>
			{/each}
		</div>

		<div class="em-peak-meter-table-wrapper">
			<PlainTable type="compact" class="em-peak-meter-table">
				<colgroup>
					<col class="em-peak-meter-col-avatar" />
					<col class="em-peak-meter-col-meter" />
					<col class="em-peak-meter-col-strongest" />
					<col class="em-peak-meter-col-live" />
					<col class="em-peak-meter-col-avg" />
					<col class="em-peak-meter-col-time" />
					<col class="em-peak-meter-col-scale" />
				</colgroup>
				<thead>
					<tr>
						<th aria-label="Device image"></th>
						<th>Meter</th>
						<th>Strongest</th>
						<th>Live peak</th>
						<th>5 min avg</th>
						<th>Last peak</th>
						<th>Peak vs scale</th>
					</tr>
				</thead>
				<tbody>
					{#each rows as row (row.panel.deviceId)}
						<tr>
							<td><EmPeakDeviceAvatar device={row.panel.device} size="s" /></td>
							<td>
								<Text styleType="body" class="text-600 em-peak-meter-name">
									{row.panel.deviceName}
								</Text>
							</td>
							<td>
								<LabelV2
									size="s"
									value={row.strongest.label}
									backgroundColor={phaseColor(row.strongest.channel)}
									iconColor="--color-white"
								/>
							</td>
							<td>
								<Text styleType="body" class="text-600" noWrap>
									{row.strongest.live?.value ?? '—'}
								</Text>
							</td>
							<td>
								<Text styleType="body-small" noWrap>
									{row.strongest.avg5?.value ?? '—'}
								</Text>
							</td>
							<td>
								<Text styleType="body-small" class="text-color-muted">
									{timeOnly(row.strongest.live?.time)}
								</Text>
							</td>
							<td>
								<div class="em-peak-meter-scale">
									<LinearGauge
										type="incremented"
										size="s"
										minValue={0}
										maxValue={maxWatts}
										hideMinMaxValue
										incrementedValue={{
											value: row.strongest.liveWatts,
											color:
												row.rank === 1 ? '--color-warning' : '--color-primary',
										}}
										a11yProps={{
											incrementedValueAriaLabel: `${row.panel.deviceName} ${row.strongest.live?.value ?? ''}`,
										}}
									/>
									<Text
										styleType="miniature"
										class="text-color-muted em-peak-meter-percent"
									>
										{Math.round(row.percent)}%
									</Text>
								</div>
							</td>
						</tr>
					{/each}
				</tbody>
			</PlainTable>
		</div>
	</div>
{/if}

<style>
	.em-peak-meter {
		display: flex;
		flex-direction: column;
		width: 100%;
		min-width: 0;
		container-type: inline-size;
		container-name: peak-meter;
	}

	.em-peak-meter-summary {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		align-items: start;
		gap: var(--spacing-s);
		padding: var(--spacing-s) var(--spacing-m);
		border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-peak-meter-summary > div {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		min-width: 0;
	}

	.em-peak-meter-summary-device {
		flex-direction: row !important;
		align-items: center;
	}

	.em-peak-meter-summary-device > div {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		min-width: 0;
	}

	.em-peak-meter-summary > div + div {
		padding-left: var(--spacing-m);
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-peak-meter-summary-value {
		color: var(--color-primary);
	}

	.em-peak-meter-table-wrapper {
		width: 100%;
		min-width: 0;
		overflow-x: auto;
		padding: 0 var(--spacing-s) var(--spacing-s);
	}

	.em-peak-meter-mobile-list {
		display: none;
	}

	:global(.em-peak-meter-table.plain-table) {
		width: 100%;
		min-width: 0;
		table-layout: fixed;
	}

	:global(.em-peak-meter-table.plain-table .em-peak-meter-col-avatar) {
		width: 2.5rem;
	}

	:global(.em-peak-meter-table.plain-table .em-peak-meter-col-meter) {
		width: 24%;
	}

	:global(.em-peak-meter-table.plain-table .em-peak-meter-col-strongest) {
		width: 18%;
	}

	:global(.em-peak-meter-table.plain-table .em-peak-meter-col-live),
	:global(.em-peak-meter-table.plain-table .em-peak-meter-col-avg),
	:global(.em-peak-meter-table.plain-table .em-peak-meter-col-time) {
		width: 12%;
	}

	:global(.em-peak-meter-table.plain-table .em-peak-meter-col-scale) {
		width: 18%;
	}

	:global(.em-peak-meter-table.plain-table thead th),
	:global(.em-peak-meter-table.plain-table tbody td) {
		padding: var(--spacing-xs) var(--spacing-xxs) !important;
		vertical-align: middle;
	}

	:global(.em-peak-meter-table.plain-table thead th:nth-child(-n + 3)),
	:global(.em-peak-meter-table.plain-table tbody td:nth-child(-n + 3)) {
		text-align: left !important;
	}

	:global(.em-peak-meter-table.plain-table thead th:nth-child(n + 4)),
	:global(.em-peak-meter-table.plain-table tbody td:nth-child(n + 4)) {
		text-align: right !important;
	}

	:global(.em-peak-meter-table.plain-table tbody tr) {
		border-top: 1px solid rgba(255, 255, 255, 0.12);
	}

	:global(.em-peak-meter-name) {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.em-peak-meter-scale {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: var(--spacing-xs);
		min-width: 0;
		margin-left: auto;
	}

	.em-peak-meter-scale :global(.linear-gauge) {
		flex: 1 1 auto;
		min-width: 0;
		max-width: 72px;
	}

	:global(.em-peak-meter-percent) {
		flex: 0 0 auto;
		min-width: 2rem;
		text-align: right;
	}

	@container peak-meter (max-width: 520px) {
		.em-peak-meter-summary {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.em-peak-meter-summary > div:nth-child(odd) {
			padding-left: 0;
			border-left: 0;
		}

		.em-peak-meter-summary > div:nth-child(n + 3) {
			padding-top: var(--spacing-s);
			border-top: 1px solid rgba(255, 255, 255, 0.12);
		}

		.em-peak-meter-summary-device {
			grid-column: 1 / -1;
			padding-left: 0 !important;
			border-left: 0 !important;
		}

		.em-peak-meter-table-wrapper {
			display: none;
		}

		.em-peak-meter-mobile-list {
			display: flex;
			flex-direction: column;
		}

		.em-peak-meter-mobile-row {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-s);
			padding: var(--spacing-s);
		}

		.em-peak-meter-mobile-row + .em-peak-meter-mobile-row {
			border-top: 1px solid rgba(255, 255, 255, 0.12);
		}

		.em-peak-meter-mobile-heading,
		.em-peak-meter-mobile-values {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-s);
			min-width: 0;
		}

		.em-peak-meter-mobile-values > div {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);
		}

		.em-peak-meter-mobile-values > div:last-child {
			align-items: flex-end;
		}

		.em-peak-meter-mobile-row > .em-peak-meter-scale {
			width: 100%;
			margin-left: 0;
		}

		.em-peak-meter-mobile-row > .em-peak-meter-scale :global(.linear-gauge) {
			max-width: none;
		}

		:global(.em-peak-meter-mobile-percent) {
			min-width: 2.5rem;
			white-space: nowrap;
			text-align: right;
		}
	}

	@container peak-meter (max-width: 900px) {
		.em-peak-meter-summary {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.em-peak-meter-summary > div:nth-child(odd) {
			padding-left: 0;
			border-left: 0;
		}

		.em-peak-meter-summary > div:nth-child(n + 3) {
			padding-top: var(--spacing-s);
			border-top: 1px solid rgba(255, 255, 255, 0.12);
		}
	}
</style>
